import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { bool, func, node, string } from 'prop-types';
import MenuItem from './MenuItem';
import MenuDivider from './MenuDivider';
import MenuGroup from './MenuGroup';
import useMenuNavigation from './useMenuNavigation';

export const MenuContext = React.createContext({});

/**
 * @deprecated Please use Dropdown from @fiverr-private/dropdown instead.
 */
const Menu = ({
    className = '',
    children = null,
    close = Function.prototype,
    isOpen = false,
    selectable = false
}) => {
    if (!children) {
        throw new Error('<Menu/> must get a children prop');
    }

    const ref = useRef();
    const { addMenuItem, removeMenuItem, hover, setHover } = useMenuNavigation({ ref });

    const context = useMemo(() => ({ close, selectable, hover, setHover, addMenuItem, removeMenuItem }),
        [close, selectable, hover, setHover, addMenuItem, removeMenuItem]);

    const handleHoverOff = useCallback(() => {
        setHover(null);
    }, [setHover]);

    return (
        <ul aria-hidden={!isOpen}
            className={classNames('p-t-8 p-b-8 p-l-0 flex flex-col', className)}
            ref={ref}
            onMouseLeave={handleHoverOff}
            tabIndex={0}>
            <MenuContext.Provider value={context}>
                {children}
            </MenuContext.Provider>
        </ul>
    );
};

Menu.displayName = 'Menu';

Menu.propTypes = {

    /**
     * The menu nodes
     */
    children: node.isRequired,

    /**
     * Close function to support being rendered inside a toggled wrapper such as dropdown
     */
    close: func,

    /**
     * isOpen to support being render inside a toggled wrapper such as dropdown
     */
    isOpen: bool,

    /**
    * Custom classes you wish to pass to the component.
    */
    className: string,

    /**
     * Whether or not the group is selectable
     */
    selectable: bool
};

Menu.Item = MenuItem;
Menu.Divider = MenuDivider;
Menu.Group = MenuGroup;

export default Menu;
